import { registerCustomSymbols } from './custom';
import { LIB } from './library';
import { newId } from './tagging';
import { normaliseUnderlay } from './underlay';
import type { Page, PanelLayout, Project, ProjectSettings, Pt, Sym, Wire, Revision } from './types';
import { t as $t } from './i18n';


export const SCHEMA_VERSION = 1;

const str = (v: unknown, max = 200) => (typeof v === 'string' ? v.slice(0, max) : v == null ? '' : String(v).slice(0, max));
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : Number(v) || 0);

export function today(): string {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

export function blankProject(name = $t("Yeni proje")): Project {
  return {
    schema: SCHEMA_VERSION, name, customer: '', author: '', date: today(), fn: 'A1', loc: 'PANO1',
    pages: [{ id: newId(), desc: $t("Güç devresi"), syms: [], wires: [] }],
  };
}

export class ProjectFormatError extends Error {}

/**
 * Validates untrusted project JSON (imports, API payloads) and returns a clean
 * copy. Unknown symbol types are dropped; malformed wires are skipped.
 */
/** Copper busbar designation: width×thickness or bars×width×thickness in mm, e.g. "80×10", "2×80×10". */
const BUS_SPEC = /^\d{1,3}(×\d{1,3}){1,2}$/;

export function normaliseProject(input: unknown): Project {
  if (!input || typeof input !== 'object') throw new ProjectFormatError($t("Proje bir JSON nesnesi olmalı."));
  const d = input as Record<string, unknown>;
  if (!Array.isArray(d.pages) || d.pages.length === 0) throw new ProjectFormatError($t("Projede en az bir sayfa olmalı."));
  // user symbols first, so their instances are recognised below
  const symbols = registerCustomSymbols(Array.isArray(d.symbols) ? d.symbols.slice(0, 500) : []);
  const pages: Page[] = d.pages.slice(0, 500).map(normalisePage);
  const st = (d.settings ?? {}) as Partial<ProjectSettings>;
  const settings: ProjectSettings = {
    wireNo: st.wireNo === 'page' || st.wireNo === 'pagecol' ? st.wireNo : 'seq',
    wireNoPot: st.wireNoPot === true,
    ...(st.standard === 'nfpa' ? { standard: 'nfpa' as const } : {}),
    ...(layersOf(st.layers) ? { layers: layersOf(st.layers) } : {}),
  };
  const rv = d.revision as Record<string, unknown> | undefined;
  const rev = (r: any): Revision | undefined =>
    r && typeof r === 'object' && str(r.label, 20)
      ? {
          label: str(r.label, 20), date: str(r.date, 20), ...(r.note ? { note: str(r.note, 500) } : {}), ...(r.by ? { by: str(r.by, 80) } : {}),
          ...(Array.isArray(r.pages) ? { pages: r.pages.slice(0, 500).map((x: unknown) => str(x, 64)).filter(Boolean) } : {}),
        }
      : undefined;
  const revision = rev(rv);
  const frame = normaliseTitleBlock(d.frame);
  const revisions: Revision[] = (Array.isArray(d.revisions) ? d.revisions.slice(0, 200) : []).map(rev).filter((r): r is Revision => !!r);
  return {
    ...(revision ? { revision } : {}),
    ...(frame ? { frame } : {}),
    ...(revisions.length ? { revisions } : {}),
    schema: SCHEMA_VERSION, settings, name: str(d.name) || $t("Adsız proje"), customer: str(d.customer), author: str(d.author),
    date: str(d.date, 20) || today(), fn: str(d.fn, 40) || 'A1', loc: str(d.loc, 40) || 'PANO1', pages,
    ...(str(d.site, 40) ? { site: str(d.site, 40) } : {}), ...(str(d.ud, 40) ? { ud: str(d.ud, 40) } : {}),
    ...(symbols.length ? { symbols } : {}),
    ...(Array.isArray(d.ignoredChecks) && d.ignoredChecks.length ? { ignoredChecks: (d.ignoredChecks as unknown[]).filter((k): k is string => typeof k === 'string').map((k) => k.slice(0, 120)).slice(0, 5000) } : {}),
  };
}

/** Validates one page (also used for macros). */
export function normalisePage(input: unknown): Page {
  const p = (input ?? {}) as any;
  const syms: Sym[] = (Array.isArray(p?.syms) ? p.syms : [])
    .filter((s: any) => s && LIB[s.type])
    .map((s: any) => ({
      id: str(s.id, 64) || newId(), type: s.type, x: num(s.x), y: num(s.y), rot: ((num(s.rot) % 4) + 4) % 4,
      tag: str(s.tag, 40), desc: str(s.desc), part: str(s.part, 80), maker: str(s.maker, 80),
      props: typeof s.props === 'object' && s.props ? { ...s.props } : {},
      ...(s.fn ? { fn: str(s.fn, 40) } : {}), ...(s.loc ? { loc: str(s.loc, 40) } : {}),
      ...(s.site ? { site: str(s.site, 40) } : {}), ...(s.ud ? { ud: str(s.ud, 40) } : {}),
    }));
  const wires: Wire[] = (Array.isArray(p?.wires) ? p.wires : [])
    .filter((w: any) => Array.isArray(w?.pts) && w.pts.length >= 2)
    .map((w: any) => ({
      id: str(w.id, 64) || newId(), pts: w.pts.map((q: any) => [num(q?.[0]), num(q?.[1])] as Pt),
      cs: str(w.cs, 10), col: str(w.col, 10), ...(w.no ? { no: str(w.no, 20) } : {}),
      ...(BUS_SPEC.test(str(w.bus, 20)) ? { bus: str(w.bus, 20) } : {}),
    }));
  const base = {
    id: str(p?.id, 64) || newId(), desc: str(p?.desc), ...(p?.fn ? { fn: str(p.fn, 40) } : {}), ...(p?.loc ? { loc: str(p.loc, 40) } : {}),
    ...(p?.site ? { site: str(p.site, 40) } : {}), ...(p?.ud ? { ud: str(p.ud, 40) } : {}),
    ...(p?.rev ? { rev: str(p.rev, 20) } : {}),
  };
  if (p?.kind === 'report') return { ...base, kind: 'report', report: { tab: str(p.report?.tab, 10), part: Math.max(0, Math.min(999, Math.floor(num(p.report?.part)))) }, syms: [], wires: [] };
  if (p?.kind === 'layout') {
    const l = p.layout ?? {};
    const pos = (v: unknown, d: number) => Math.max(1, Math.min(10000, num(v) || d));
    const arr = (v: unknown) => (Array.isArray(v) ? v.slice(0, 2000) : []);
    const layout: PanelLayout = {
      ...(l.door === true ? { door: true } : {}),
      ...(l.door === true && typeof l.inside === 'string' && l.inside ? { inside: str(l.inside, 64) } : {}),
      w: pos(l.w, 600), h: pos(l.h, 800), scale: pos(l.scale, 5),
      ...(num(l.depth) > 0 ? { depth: pos(l.depth, 250) } : {}),
      rails: arr(l.rails).map((r: any) => ({ id: str(r?.id, 64) || newId(), x: num(r?.x), y: num(r?.y), len: pos(r?.len, 200) })),
      ducts: arr(l.ducts).map((d: any) => ({ id: str(d?.id, 64) || newId(), x: num(d?.x), y: num(d?.y), w: pos(d?.w, 40), h: pos(d?.h, 40) })),
      items: arr(l.items).map((i: any) => ({ id: str(i?.id, 64) || newId(), dev: str(i?.dev, 120), x: num(i?.x), y: num(i?.y) })),
    };
    const bars = arr(l.bars).slice(0, 10).filter((b: any) => BUS_SPEC.test(str(b?.spec, 20)) && BUS_SPEC.test(str(b?.pe, 20)));
    if (bars.length)
      layout.bars = bars.map((b: any) => ({
        x: num(b.x), y: num(b.y), w: pos(b.w, 500), spec: str(b.spec, 20), pe: str(b.pe, 20), ...(b.n ? { n: str(b.n, 20) } : {}),
        ...(b.mid && typeof b.mid === 'object'
          ? { mid: { nY: num(b.mid.nY), peY: num(b.mid.peY), ...(Array.isArray(b.mid.nSpan) ? { nSpan: [num(b.mid.nSpan[0]), num(b.mid.nSpan[1])] as [number, number] } : {}), ...(Array.isArray(b.mid.peSpan) ? { peSpan: [num(b.mid.peSpan[0]), num(b.mid.peSpan[1])] as [number, number] } : {}) } }
          : {}),
      }));
    const beams = arr(l.beams).slice(0, 200).map((b: any) => ({ id: str(b?.id, 64) || newId(), x: num(b?.x), y: num(b?.y), w: pos(b?.w, 500) }));
    if (beams.length) layout.beams = beams;
    const links = arr(l.links)
      .slice(0, 40)
      .map((k: any) => ({ pts: arr(k?.pts).slice(0, 20).map((p: any) => [num(p?.[0]), num(p?.[1])] as [number, number]), spec: str(k?.spec, 20), ...(k?.kind === 'N' ? { kind: 'N' as const } : {}), ...([0, 1, 2].includes(k?.ph) ? { ph: k.ph as number } : {}) }))
      .filter((k: { pts: unknown[] }) => k.pts.length >= 2);
    if (links.length) layout.links = links;
    const secs = arr(l.sections).slice(0, 40).map((v) => num(v)).filter((v) => v >= 100 && v <= 3000);
    if (secs.length) layout.sections = secs;
    const names = arr(l.sectionNames).slice(0, secs.length).map((v) => str(v, 40));
    if (secs.length && names.some(Boolean)) layout.sectionNames = names;
    return { ...base, kind: 'layout', layout, syms: [], wires: [] };
  }
  const underlay = normaliseUnderlay(p?.underlay);
  return { ...base, ...(p?.kind === 'single' ? { kind: 'single' as const } : {}), ...(underlay ? { underlay } : {}), syms, wires };
}

/** Largest logo kept in a title block (data URL length). */
export const MAX_LOGO = 400_000;

/** Cleans a title block; undefined when nothing is set. */
export function normaliseTitleBlock(v: unknown): import('./types').TitleBlock | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const d = v as Record<string, unknown>;
  const s = (x: unknown, n: number) => (typeof x === 'string' ? x.trim().slice(0, n) : '');
  const logo = typeof d.logo === 'string' && d.logo.length <= MAX_LOGO && /^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/.test(d.logo) ? d.logo : '';
  const fields = (Array.isArray(d.fields) ? d.fields : [])
    .map((f: any) => ({ label: s(f?.label, 24), value: s(f?.value, 60) }))
    .filter((f) => f.label || f.value)
    .slice(0, 2);
  const out = {
    ...(s(d.company, 80) ? { company: s(d.company, 80) } : {}),
    ...(s(d.address, 120) ? { address: s(d.address, 120) } : {}),
    ...(logo ? { logo } : {}),
    ...(s(d.drawingNo, 40) ? { drawingNo: s(d.drawingNo, 40) } : {}),
    ...(fields.length ? { fields } : {}),
  };
  return Object.keys(out).length ? out : undefined;
}

const LAYER_NAMES = ['frame', 'wire', 'symbol', 'tag', 'desc', 'pin', 'ref', 'mirror', 'wno', 'label', 'underlay'];
/** Keeps known layers with a hide / noprint flag set. */
function layersOf(v: unknown): Record<string, { hide?: boolean; noprint?: boolean }> | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const out: Record<string, { hide?: boolean; noprint?: boolean }> = {};
  for (const [k, x] of Object.entries(v as Record<string, any>))
    if (LAYER_NAMES.includes(k) && x && (x.hide === true || x.noprint === true)) out[k] = { ...(x.hide === true ? { hide: true } : {}), ...(x.noprint === true ? { noprint: true } : {}) };
  return Object.keys(out).length ? out : undefined;
}
